// Helper to detect which price field should be evaluated during validation.
const resolvePriceFieldKey = (artworkState, overrideKey) => {
  if (overrideKey) return overrideKey;
  const about = artworkState?.artwork_about ?? {};
  if (about.artwork_is_auction || Object.prototype.hasOwnProperty.call(about, 'price_starting_at')) {
    return 'price_starting_at';
  }
  return 'artwork_price';
};

export const validateArtworkFields = (artworkState, options = {}) => {
  const errors = [];
  const priceFieldKey = resolvePriceFieldKey(artworkState, options.priceFieldKey);
  const priceValue = artworkState?.artwork_about?.[priceFieldKey];
  const numericPrice = Number(priceValue);
  const hasNumericPrice = Number.isFinite(numericPrice);
    
  if (!artworkState.artwork_about.artwork_artist) {
    errors.push({ element: 'artwork_artist', error_code: 'EMPTY_FIELD' });
  }

  if (!artworkState.artwork_about.artwork_type) {
    errors.push({ element: 'artwork_type', error_code: 'EMPTY_FIELD' });
  }

  if (!artworkState.artwork_about.artwork_title) {
    errors.push({ element: 'artwork_title', error_code: 'EMPTY_FIELD' });
  }

  if (artworkState.artwork_about.artwork_title.length < 3) {
    errors.push({ element: 'artwork_title', error_code: 'TITLE_TOO_SHORT' });
  }

  if (!artworkState.artwork_about.artwork_description) {
    errors.push({ element: 'artwork_description', error_code: 'EMPTY_FIELD' });
  }

  if (artworkState.artwork_about.artwork_description.length < 10) {
    errors.push({ element: 'artwork_description', error_code: 'DESCRIPTION_TOO_SHORT' });
  }

  if (!artworkState.artwork_about.artwork_medium) {
    errors.push({ element: 'artwork_medium', error_code: 'EMPTY_FIELD' });
  }

  if (artworkState.artwork_about.artwork_theme.length === 0) {
    errors.push({ element: 'artwork_theme', error_code: 'EMPTY_FIELD' });
  }

  if (artworkState.artwork_about.artwork_feeling.length === 0) {
    errors.push({ element: 'artwork_feeling', error_code: 'EMPTY_FIELD' });
  }

  if (artworkState.artwork_about.artwork_color.length === 0) {
    errors.push({ element: 'artwork_color', error_code: 'EMPTY_FIELD' });
  }

  if (!artworkState.artwork_about.artwork_year) {
    errors.push({ element: 'artwork_year', error_code: 'EMPTY_FIELD' });
  }

  if (!artworkState.artwork_about.artwork_size.length) {
    errors.push({ element: 'artwork_length', error_code: 'EMPTY_FIELD' });
  }

  if (!artworkState.artwork_about.artwork_size.height) {
    errors.push({ element: 'artwork_height', error_code: 'EMPTY_FIELD' });
  }

  if (artworkState.artwork_about.artwork_size.length <= 0) {
    errors.push({ element: 'artwork_length', error_code: 'INVALID_SIZE' });
  }

  if (artworkState.artwork_about.artwork_size.height <= 0) {
    errors.push({ element: 'artwork_height', error_code: 'INVALID_SIZE' });
  }

  if (!artworkState.artwork_about.artwork_currency) {
    errors.push({ element: 'artwork_currency', error_code: 'EMPTY_FIELD' });
  }

  if (!priceValue) {
    errors.push({ element: priceFieldKey, error_code: 'EMPTY_FIELD' });
  }

  if (hasNumericPrice && numericPrice <= 0) {
    errors.push({ element: priceFieldKey, error_code: 'INVALID_PRICE' });
  }

  if (hasNumericPrice && numericPrice <= 99) {
    errors.push({ element: priceFieldKey, error_code: 'INVALID_PRICE_LOWER_THAN_100' });
  }

  if (hasNumericPrice && numericPrice < (artworkState.artwork_about.artwork_original_price - (artworkState.artwork_about.artwork_original_price * 0.3))) {
    errors.push({ element: priceFieldKey, error_code: 'INVALID_PRICE_DOWNGRADE' });
  }

  return errors;
};

export const getPriceFieldKey = (artworkState) => resolvePriceFieldKey(artworkState);

/**
 * ----------  DETECCIÓN DE CAMBIOS CRÍTICOS  ----------
 * Devuelve true si los cambios requieren revisión manual.
 */
export const hasCriticalChanges = (original, current) => {
  if (!original || !current) return false;

  // 1. Artista
  const originalArtistId =
    original.artwork_about?.artwork_artist?._id ??
    original.artwork_about?.artwork_gallery_artist?._id;
  const currentArtistId = current.artwork_about?.artwork_artist?._id;
  const artistChanged = originalArtistId !== currentArtistId;

  // 2. Imagen principal
  const originalImageId =
    original.artwork_media?.artwork_main_picture?.image_id ?? null;
  const currentImageId =
    current.artwork_media?.artwork_main_picture?.image_id ?? null;
  const mainPictureChanged = originalImageId !== currentImageId;

  // 3. Dimensiones (> 5 cm)
  const cm = (v, unit) => (unit === 'inches' ? parseFloat(v) * 2.54 : parseFloat(v));
  const oSize = original.artwork_about?.artwork_size ?? {};
  const cSize = current.artwork_about?.artwork_size ?? {};
  const lengthDiff = Math.abs(cm(oSize.length, oSize.unit) - cm(cSize.length, cSize.unit));
  const heightDiff = Math.abs(cm(oSize.height, oSize.unit) - cm(cSize.height, cSize.unit));
  const sizeChanged = lengthDiff >= 5 || heightDiff >= 5;

  return artistChanged || mainPictureChanged || sizeChanged;
};